import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useDispatch } from 'react-redux';
import { apiPost } from '@/api/client';
import { tokenReceived } from '@/store/authSlice';
import type { AppDispatch } from '@/store/store';
import { env, fetchAuthConfig } from '@/env';
import {
  SsoButtons,
  CodeCells,
  useCountdown,
  formatCountdown,
  CODE_LENGTH,
  CODE_TTL_SECONDS,
  RESEND_DELAY_SECONDS,
  type SsoProviderConfig,
  type CodeCellsHandle,
} from '@foundation-bb/foundation-auth-ui';
import {
  Page,
  Card,
  LogoWrapper,
  Logo,
  LogoText,
  Title,
  Subtitle,
  Form,
  FieldGroup,
  Label,
  Input,
  SubmitButton,
  ErrorMessage,
  SuccessMessage,
  CodeCellsWrapper,
  LinkButton,
  Timer,
  ResendRow,
  ResendCaption,
  ResendLinkRow,
  ButtonSpinner,
} from './LoginPage.styled';

interface VerifyResponse {
  accessToken: string;
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(() => localStorage.getItem('scribia_login_email') ?? '');
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeStartedAt, setCodeStartedAt] = useState<number | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [ssoConfig, setSsoConfig] = useState<SsoProviderConfig>({ google: false, microsoft: false });

  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const turnstileTokenRef = useRef<string>('');
  const codeCellsRef = useRef<CodeCellsHandle | null>(null);

  // Foundation-derived countdown (replaces the bespoke interval + COUNTDOWN_SECONDS).
  const { remainingSeconds, elapsedSeconds } = useCountdown(
    codeStartedAt,
    CODE_TTL_SECONDS,
    step === 'code',
  );
  const resendAvailable = step === 'code' && elapsedSeconds >= RESEND_DELAY_SECONDS;

  // Load turnstile site key
  useEffect(() => {
    fetchAuthConfig().then((config) => {
      setTurnstileSiteKey(config.turnstileSiteKey);
      setTurnstileEnabled(config.turnstileEnabled);
      setSsoConfig(config.sso);
    });
  }, []);

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const turnstileToken = turnstileTokenRef.current;
      await apiPost('/auth/request-code', { email, turnstileToken });
      localStorage.setItem('scribia_login_email', email);
      setCode(Array(CODE_LENGTH).fill(''));
      setStep('code');
      setCodeStartedAt(Date.now());
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 429) {
        setError(t('auth.code.rateLimited'));
      } else {
        setError(t('auth.code.errorGeneric'));
      }
    } finally {
      setIsSubmitting(false);
      turnstileTokenRef.current = '';

      turnstileRef.current?.reset();
    }
  };

  const submitCode = async (codeValue: string[]) => {
    const fullCode = codeValue.join('').toUpperCase();
    if (fullCode.length !== CODE_LENGTH) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const turnstileToken = turnstileTokenRef.current;
      const result = await apiPost<VerifyResponse>('/auth/verify-code', {
        email,
        code: fullCode,
        turnstileToken,
      });
      dispatch(tokenReceived({ accessToken: result.accessToken }));

      if ('PasswordCredential' in window) {
        const cred = new (window as never as { PasswordCredential: typeof Credential & { new(data: { id: string; password: string }): Credential } }).PasswordCredential({ id: email, password: fullCode });
        navigator.credentials.store(cred).catch(() => {});
      }

      navigate('/sprawy', { replace: true });
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 410) {
        setError(t('auth.code.expired'));
      } else if (status === 401 || status === 400) {
        setError(t('auth.code.invalid'));
      } else {
        setError(t('auth.code.errorGeneric'));
      }
      setCode(Array(CODE_LENGTH).fill(''));
      codeCellsRef.current?.focusFirst();
    } finally {
      setIsSubmitting(false);
      turnstileTokenRef.current = '';

      turnstileRef.current?.reset();
    }
  };

  const handleResend = async () => {
    setError(null);

    try {
      const turnstileToken = turnstileTokenRef.current;
      await apiPost('/auth/request-code', { email, turnstileToken });
      // Restart countdown
      setCodeStartedAt(Date.now());
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 429) {
        setError(t('auth.code.rateLimited'));
      } else {
        setError(t('auth.code.errorGeneric'));
      }
    } finally {
      turnstileTokenRef.current = '';

      turnstileRef.current?.reset();
    }
  };

  // Scribia-specific: deliver the code over SMS. Not lifted into Foundation.
  const handleResendSms = async () => {
    setError(null);

    try {
      const turnstileToken = turnstileTokenRef.current;
      await apiPost('/auth/request-code', { email, turnstileToken, channel: 'sms' });
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 429) {
        setError(t('auth.code.rateLimited'));
      } else {
        setError(t('auth.code.errorGeneric'));
      }
    } finally {
      turnstileTokenRef.current = '';

      turnstileRef.current?.reset();
    }
  };

  const handleDifferentEmail = () => {
    setStep('email');
    setCode(Array(CODE_LENGTH).fill(''));
    setError(null);
    setCodeStartedAt(null);
  };

  return (
    <Page>
      <Card>
        <LogoWrapper>
          <Logo src="/logo_350_350.png" alt="Scribia" />
          <LogoText>Scribia</LogoText>
        </LogoWrapper>
        <Title>{t('auth.code.title')}</Title>

        {step === 'email' && (
          <>
            <Subtitle>{t('auth.code.subtitle')}</Subtitle>
            <Form onSubmit={handleSendCode}>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <FieldGroup>
                <Label htmlFor="email">{t('auth.login.email')}</Label>
                <Input
                  id="email"
                  name="username"
                  type="email"
                  autoComplete="username"
                  placeholder={t('auth.code.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && email && !isSubmitting) {
                      e.preventDefault();
                      handleSendCode(e as unknown as FormEvent);
                    }
                  }}
                  required
                />
              </FieldGroup>
              {turnstileEnabled && turnstileSiteKey && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  options={{ size: 'invisible' }}
                  onSuccess={(token) => { turnstileTokenRef.current = token;}}
                  onExpire={() => { turnstileTokenRef.current = '';}}
                  onError={() => {}}
                />
              )}
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><ButtonSpinner />{t('auth.code.sending')}</>
                ) : (
                  t('auth.code.send')
                )}
              </SubmitButton>
            </Form>
            {(ssoConfig.google || ssoConfig.microsoft) && (
              <SsoButtons
                apiBase={env.api.baseUrl}
                returnUrl={`${window.location.origin}/documents`}
                enabled={ssoConfig}
                labels={{
                  google: t('auth.sso.continueWithGoogle'),
                  microsoft: t('auth.sso.continueWithMicrosoft'),
                }}
                style={{ marginTop: '1rem' }}
              />
            )}
          </>
        )}

        {step === 'code' && (
          <>
            <SuccessMessage>
              {t('auth.code.sent')} <strong>{email}</strong>
            </SuccessMessage>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                submitCode(code);
              }}
              $spaced
            >
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <CodeCellsWrapper>
                <CodeCells
                  ref={codeCellsRef}
                  code={code}
                  onChange={setCode}
                  onComplete={submitCode}
                  disabled={isSubmitting}
                  classNames={{ container: 'scribia-code-row', cell: 'scribia-code-cell' }}
                  cellAriaLabel={(i) => t('auth.code.digitLabel', { n: i + 1 })}
                  cellTestId={(i) => `code-input-${i}`}
                />
              </CodeCellsWrapper>

              {turnstileEnabled && turnstileSiteKey && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  options={{ size: 'invisible' }}
                  onSuccess={(token) => { turnstileTokenRef.current = token;}}
                  onExpire={() => { turnstileTokenRef.current = '';}}
                  onError={() => {}}
                />
              )}

              <SubmitButton type="submit" disabled={isSubmitting || code.join('').length !== CODE_LENGTH}>
                {isSubmitting ? t('auth.code.verifying') : t('auth.code.verify')}
              </SubmitButton>

              {remainingSeconds > 0 ? (
                <Timer>{formatCountdown(remainingSeconds)}</Timer>
              ) : (
                <ErrorMessage>{t('auth.code.expired')}</ErrorMessage>
              )}

              <ResendRow>
                {resendAvailable && (
                  <>
                    <ResendCaption>{t('auth.code.didntReceive')}</ResendCaption>
                    <ResendLinkRow>
                      <LinkButton type="button" onClick={handleResend}>
                        {t('auth.code.resend')}
                      </LinkButton>
                      <LinkButton type="button" onClick={handleResendSms}>
                        {t('auth.code.sendSms')}
                      </LinkButton>
                    </ResendLinkRow>
                  </>
                )}
                <LinkButton type="button" onClick={handleDifferentEmail}>
                  {t('auth.code.differentEmail')}
                </LinkButton>
              </ResendRow>
            </Form>
          </>
        )}
      </Card>
    </Page>
  );
}
